import { useState, useEffect, useRef } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useAuth, ProtectedRoute } from '../../components/auth/AuthContext'
import RichTextEditor from '../../components/editor/RichTextEditor'

function ArticleEditorContent() {
  const { id } = useParams<{ id: string }>()
  const isEditing = Boolean(id)
  const navigate = useNavigate()
  const { token } = useAuth()
  
  const [isLoading, setIsLoading] = useState(isEditing)
  const [isSaving, setIsSaving] = useState(false)
  const [activeSection, setActiveSection] = useState<'content' | 'seo' | 'settings' | 'analytics'>('content')
  const [hasChanges, setHasChanges] = useState(false)
  const [saveMessage, setSaveMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)

  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    excerpt: '',
    content: '',
    image: '',
    category: 'Risparmio',
    author: 'Carletto',
    readTime: 5,
    published: false,
    seo: {
      metaTitle: '',
      metaDescription: '',
      keywords: [] as string[],
      canonicalUrl: '',
      ogImage: '',
      noIndex: false,
    },
  })

  const [keywordsInput, setKeywordsInput] = useState('')

  // Fetch post data if editing
  useEffect(() => {
    if (isEditing && id) {
      fetchPost(id)
    }
  }, [id, isEditing])

  const fetchPost = async (postId: string) => {
    try {
      const response = await fetch(`/api/posts/admin/${postId}`)
      if (!response.ok) throw new Error('Post not found')
      const post = await response.json()
      
      setFormData({
        title: post.title || '',
        slug: post.slug || '',
        excerpt: post.excerpt || '',
        content: post.content || '',
        image: post.image || '',
        category: post.category || 'Risparmio',
        author: post.author || 'Carletto',
        readTime: post.readTime || 5,
        published: post.published || false,
        seo: {
          metaTitle: post.seo?.metaTitle || '',
          metaDescription: post.seo?.metaDescription || '',
          keywords: post.seo?.keywords || [],
          canonicalUrl: post.seo?.canonicalUrl || '',
          ogImage: post.seo?.ogImage || '',
          noIndex: post.seo?.noIndex || false,
        },
      })
      setKeywordsInput(post.seo?.keywords?.join(', ') || '')
    } catch (error) {
      console.error('Error fetching post:', error)
      setSaveMessage({ type: 'error', text: 'Errore nel caricamento dell\'articolo' })
    } finally {
      setIsLoading(false)
    }
  }

  const updateFormData = (updates: Partial<typeof formData>) => {
    setFormData(prev => ({ ...prev, ...updates }))
    setHasChanges(true)
  }

  const updateSeoData = (updates: Partial<typeof formData.seo>) => {
    setFormData(prev => ({ ...prev, seo: { ...prev.seo, ...updates } }))
    setHasChanges(true)
  }

  const generateSlug = (title: string) => {
    return title
      .toLowerCase()
      .replace(/[àáâãäå]/g, 'a')
      .replace(/[èéêë]/g, 'e')
      .replace(/[ìíîï]/g, 'i')
      .replace(/[òóôõö]/g, 'o')
      .replace(/[ùúûü]/g, 'u')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '')
  }

  const autoGenerateSEO = () => {
    updateSeoData({
      metaTitle: formData.seo.metaTitle || formData.title,
      metaDescription: formData.seo.metaDescription || formData.excerpt.slice(0, 160),
      ogImage: formData.seo.ogImage || formData.image,
    })
  }

  const handleSave = async (publish?: boolean) => {
    if (!formData.title || !formData.slug || !formData.excerpt || !formData.content) {
      setSaveMessage({ type: 'error', text: 'Compila tutti i campi obbligatori' })
      return
    }

    setIsSaving(true)
    setSaveMessage(null)

    try {
      const keywords = keywordsInput
        .split(',')
        .map(k => k.trim())
        .filter(k => k.length > 0)

      const postData = {
        ...formData,
        published: publish !== undefined ? publish : formData.published,
        seo: {
          ...formData.seo,
          keywords,
        },
      }

      const url = isEditing ? `/api/posts/${id}` : '/api/posts'
      const method = isEditing ? 'PUT' : 'POST'

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(postData),
      })

      if (!response.ok) throw new Error('Failed to save')

      const savedPost = await response.json()
      setHasChanges(false)
      setSaveMessage({ type: 'success', text: isEditing ? 'Articolo aggiornato!' : 'Articolo creato!' })

      // If creating new, redirect to edit page
      if (!isEditing) {
        setTimeout(() => {
          navigate(`/admin/articles/${savedPost.id}/edit`)
        }, 1000)
      }
    } catch (error) {
      console.error('Error saving post:', error)
      setSaveMessage({ type: 'error', text: 'Errore durante il salvataggio' })
    } finally {
      setIsSaving(false)
    }
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#F8B120]"></div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-4">
              <Link 
                to="/admin/articles" 
                className="text-gray-500 hover:text-gray-700 flex items-center"
              >
                <svg className="h-5 w-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Articoli
              </Link>
              <span className="text-gray-300">|</span>
              <h1 className="text-lg font-semibold text-gray-900">
                {isEditing ? 'Modifica Articolo' : 'Nuovo Articolo'}
              </h1>
              {hasChanges && (
                <span className="text-xs text-orange-500 bg-orange-50 px-2 py-1 rounded">
                  Modifiche non salvate
                </span>
              )}
            </div>

            <div className="flex items-center space-x-3">
              {saveMessage && (
                <span className={`text-sm ${saveMessage.type === 'success' ? 'text-green-600' : 'text-red-600'}`}>
                  {saveMessage.text}
                </span>
              )}
              
              <button
                onClick={() => handleSave(false)}
                disabled={isSaving}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
              >
                {isSaving ? 'Salvataggio...' : 'Salva Bozza'}
              </button>
              
              <button
                onClick={() => handleSave(true)}
                disabled={isSaving}
                className="px-4 py-2 bg-[#F8B120] text-white rounded-lg hover:bg-[#e5a11d] transition-colors disabled:opacity-50"
              >
                {formData.published ? 'Aggiorna' : 'Pubblica'}
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex gap-8">
          {/* Sidebar Navigation */}
          <div className="w-48 shrink-0">
            <nav className="sticky top-24 space-y-1">
              <SidebarButton
                active={activeSection === 'content'}
                onClick={() => setActiveSection('content')}
                icon={
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                }
              >
                Contenuto
              </SidebarButton>
              
              <SidebarButton
                active={activeSection === 'seo'}
                onClick={() => setActiveSection('seo')}
                icon={
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                }
                badge={!formData.seo.metaTitle}
              >
                SEO
              </SidebarButton>
              
              <SidebarButton
                active={activeSection === 'settings'}
                onClick={() => setActiveSection('settings')}
                icon={
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                }
              >
                Impostazioni
              </SidebarButton>

              {isEditing && (
                <SidebarButton
                  active={activeSection === 'analytics'}
                  onClick={() => setActiveSection('analytics')}
                  icon={
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                  }
                >
                  Analytics
                </SidebarButton>
              )}

              {/* Status indicator */}
              <div className="pt-6 mt-6 border-t border-gray-200">
                <div className="text-xs text-gray-500 uppercase tracking-wide mb-2">Stato</div>
                <div className={`inline-flex items-center px-2.5 py-1 rounded-full text-sm font-medium ${
                  formData.published 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-yellow-100 text-yellow-800'
                }`}>
                  <span className={`w-2 h-2 rounded-full mr-2 ${
                    formData.published ? 'bg-green-500' : 'bg-yellow-500'
                  }`}></span>
                  {formData.published ? 'Pubblicato' : 'Bozza'}
                </div>
              </div>
            </nav>
          </div>

          {/* Main Content */}
          <div className="flex-1 min-w-0">
            <motion.div
              key={activeSection}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2 }}
            >
              {activeSection === 'content' && (
                <ContentSection
                  formData={formData}
                  updateFormData={updateFormData}
                  generateSlug={generateSlug}
                />
              )}

              {activeSection === 'seo' && (
                <SEOSection
                  formData={formData}
                  updateSeoData={updateSeoData}
                  keywordsInput={keywordsInput}
                  setKeywordsInput={(v) => { setKeywordsInput(v); setHasChanges(true); }}
                  autoGenerateSEO={autoGenerateSEO}
                />
              )}

              {activeSection === 'settings' && (
                <SettingsSection
                  formData={formData}
                  updateFormData={updateFormData}
                />
              )}

              {activeSection === 'analytics' && isEditing && (
                <AnalyticsSection postId={id!} />
              )}
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  )
}

// Sidebar Button Component
function SidebarButton({
  children,
  active,
  onClick,
  icon,
  badge,
}: {
  children: React.ReactNode
  active: boolean
  onClick: () => void
  icon: React.ReactNode
  badge?: boolean
}) {
  return (
    <button
      onClick={onClick}
      className={`w-full flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
        active
          ? 'bg-[#F8B120] text-white'
          : 'text-gray-600 hover:bg-gray-100'
      }`}
    >
      <span className="mr-3">{icon}</span>
      {children}
      {badge && (
        <span className="ml-auto w-2 h-2 bg-red-500 rounded-full"></span>
      )}
    </button>
  )
}

// Content Section
function ContentSection({
  formData,
  updateFormData,
  generateSlug,
}: {
  formData: any
  updateFormData: (updates: any) => void
  generateSlug: (title: string) => string
}) {
  return (
    <div className="space-y-6">
      {/* Title */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Titolo *
        </label>
        <input
          type="text"
          value={formData.title}
          onChange={e => {
            updateFormData({
              title: e.target.value,
              slug: formData.slug || generateSlug(e.target.value),
            })
          }}
          placeholder="Inserisci il titolo dell'articolo"
          className="w-full px-4 py-3 text-lg border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F8B120] focus:border-transparent"
        />
      </div>

      {/* Slug */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          URL Slug *
        </label>
        <div className="flex items-center">
          <span className="text-gray-500 text-sm mr-2">bollettaamica.com/blog/</span>
          <input
            type="text"
            value={formData.slug}
            onChange={e => updateFormData({ slug: e.target.value })}
            placeholder="url-articolo"
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F8B120] focus:border-transparent"
          />
        </div>
      </div>

      {/* Cover Image */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Immagine di Copertina
        </label>
        <ImageUpload
          value={formData.image}
          onChange={url => updateFormData({ image: url })}
        />
      </div>

      {/* Excerpt */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Estratto *
          <span className="ml-2 text-xs text-gray-400 font-normal">
            Breve descrizione mostrata nelle anteprime
          </span>
        </label>
        <textarea
          value={formData.excerpt}
          onChange={e => updateFormData({ excerpt: e.target.value })}
          placeholder="Scrivi un breve riassunto dell'articolo (2-3 frasi)"
          rows={3}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F8B120] focus:border-transparent resize-none"
        />
        <div className="mt-1 text-xs text-gray-500 text-right">
          {formData.excerpt.length}/200 caratteri
        </div>
      </div>

      {/* Content Editor */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Contenuto *
        </label>
        <RichTextEditor
          content={formData.content}
          onChange={content => updateFormData({ content })}
          placeholder="Inizia a scrivere il tuo articolo..."
        />
      </div>
    </div>
  )
}

// SEO Section
function SEOSection({
  formData,
  updateSeoData,
  keywordsInput,
  setKeywordsInput,
  autoGenerateSEO,
}: {
  formData: any
  updateSeoData: (updates: any) => void
  keywordsInput: string
  setKeywordsInput: (value: string) => void
  autoGenerateSEO: () => void
}) {
  return (
    <div className="space-y-6">
      {/* Auto-generate */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-center justify-between">
        <div>
          <h3 className="font-medium text-blue-900">Genera automaticamente</h3>
          <p className="text-sm text-blue-700">Compila i campi SEO basandoti sul contenuto dell'articolo</p>
        </div>
        <button
          onClick={autoGenerateSEO}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center"
        >
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
          Auto-compila
        </button>
      </div>

      {/* Meta Title */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Meta Title
          <span className={`ml-2 text-xs ${formData.seo.metaTitle.length > 60 ? 'text-red-500' : 'text-gray-400'}`}>
            ({formData.seo.metaTitle.length}/60)
          </span>
        </label>
        <input
          type="text"
          value={formData.seo.metaTitle}
          onChange={e => updateSeoData({ metaTitle: e.target.value })}
          placeholder="Titolo per i motori di ricerca"
          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-[#F8B120] focus:border-transparent ${
            formData.seo.metaTitle.length > 60 ? 'border-red-300' : 'border-gray-300'
          }`}
        />
        {formData.seo.metaTitle.length > 60 && (
          <p className="mt-1 text-xs text-red-600">Il titolo e troppo lungo per Google</p>
        )}
      </div>

      {/* Meta Description */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Meta Description
          <span className={`ml-2 text-xs ${formData.seo.metaDescription.length > 160 ? 'text-red-500' : 'text-gray-400'}`}>
            ({formData.seo.metaDescription.length}/160)
          </span>
        </label>
        <textarea
          value={formData.seo.metaDescription}
          onChange={e => updateSeoData({ metaDescription: e.target.value })}
          placeholder="Descrizione per i motori di ricerca"
          rows={3}
          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-[#F8B120] focus:border-transparent resize-none ${
            formData.seo.metaDescription.length > 160 ? 'border-red-300' : 'border-gray-300'
          }`}
        />
      </div>

      {/* Keywords */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Keywords
        </label>
        <input
          type="text"
          value={keywordsInput}
          onChange={e => setKeywordsInput(e.target.value)}
          placeholder="risparmio energia, bollette, gas, luce"
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F8B120] focus:border-transparent"
        />
        <p className="mt-1 text-xs text-gray-500">Separa le keywords con una virgola</p>
      </div>

      {/* Canonical URL */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Canonical URL
          <span className="ml-2 text-xs text-gray-400 font-normal">Opzionale</span>
        </label>
        <input
          type="url"
          value={formData.seo.canonicalUrl}
          onChange={e => updateSeoData({ canonicalUrl: e.target.value })}
          placeholder="https://bollettaamica.com/blog/..."
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F8B120] focus:border-transparent"
        />
        <p className="mt-1 text-xs text-gray-500">Lascia vuoto per usare l'URL di default</p>
      </div>

      {/* OG Image */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Open Graph Image
          <span className="ml-2 text-xs text-gray-400 font-normal">Per i social media (1200x630px)</span>
        </label>
        <ImageUpload
          value={formData.seo.ogImage || ''}
          onChange={url => updateSeoData({ ogImage: url })}
        />
        <p className="mt-2 text-xs text-gray-500">Se vuoto, verra usata l'immagine di copertina</p>
      </div>

      {/* noIndex */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <label className="flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={formData.seo.noIndex}
            onChange={e => updateSeoData({ noIndex: e.target.checked })}
            className="h-5 w-5 text-[#F8B120] focus:ring-[#F8B120] border-gray-300 rounded"
          />
          <span className="ml-3">
            <span className="text-sm font-medium text-gray-700">Non indicizzare (noindex)</span>
            <span className="block text-xs text-gray-500">Impedisce a Google di indicizzare questa pagina</span>
          </span>
        </label>
      </div>

      {/* Google Preview */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <h3 className="text-sm font-medium text-gray-700 mb-4">Anteprima su Google</h3>
        <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
          <div className="text-xl text-blue-700 hover:underline cursor-pointer line-clamp-1">
            {formData.seo.metaTitle || formData.title || 'Titolo dell\'articolo'}
          </div>
          <div className="text-sm text-green-700 mt-1">
            bollettaamica.com/blog/{formData.slug || 'url-articolo'}
          </div>
          <div className="text-sm text-gray-600 mt-1 line-clamp-2">
            {formData.seo.metaDescription || formData.excerpt || 'Descrizione dell\'articolo che apparira nei risultati di ricerca...'}
          </div>
        </div>
      </div>
    </div>
  )
}

// Settings Section
function SettingsSection({
  formData,
  updateFormData,
}: {
  formData: any
  updateFormData: (updates: any) => void
}) {
  return (
    <div className="space-y-6">
      {/* Category */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Categoria
        </label>
        <select
          value={formData.category}
          onChange={e => updateFormData({ category: e.target.value })}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F8B120] focus:border-transparent"
        >
          <option value="Risparmio">Risparmio</option>
          <option value="Energia">Energia</option>
          <option value="Guide">Guide</option>
          <option value="Novita">Novita</option>
        </select>
      </div>

      {/* Author */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Autore
        </label>
        <input
          type="text"
          value={formData.author}
          onChange={e => updateFormData({ author: e.target.value })}
          placeholder="Nome autore"
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F8B120] focus:border-transparent"
        />
      </div>

      {/* Read Time */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Tempo di lettura (minuti)
        </label>
        <input
          type="number"
          value={formData.readTime}
          onChange={e => updateFormData({ readTime: parseInt(e.target.value) || 1 })}
          min="1"
          max="60"
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F8B120] focus:border-transparent"
        />
      </div>

      {/* Publish Status */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <label className="flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={formData.published}
            onChange={e => updateFormData({ published: e.target.checked })}
            className="h-5 w-5 text-[#F8B120] focus:ring-[#F8B120] border-gray-300 rounded"
          />
          <span className="ml-3">
            <span className="text-sm font-medium text-gray-700">Pubblicato</span>
            <span className="block text-xs text-gray-500">L'articolo sara visibile pubblicamente</span>
          </span>
        </label>
      </div>
    </div>
  )
}

// Analytics Section
interface AnalyticsData {
  postId: string
  total: number
  today: number
  yesterday: number
  last7Days: number
  last30Days: number
  dailyBreakdown: { date: string; views: number }[]
  trend: 'up' | 'down' | 'stable'
}

function AnalyticsSection({ postId }: { postId: string }) {
  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    fetchAnalytics()
  }, [postId])

  const fetchAnalytics = async () => {
    try {
      setIsLoading(true)
      const response = await fetch(`/api/analytics/${postId}`)
      if (!response.ok) throw new Error('Failed to fetch analytics')
      const data = await response.json()
      setAnalytics(data)
    } catch (err) {
      console.error('Error fetching analytics:', err)
      setError('Impossibile caricare le statistiche')
    } finally {
      setIsLoading(false)
    }
  }

  if (isLoading) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-8 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#F8B120]"></div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-8 text-center">
        <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
        <p className="text-gray-500">{error}</p>
        <button
          onClick={fetchAnalytics}
          className="mt-4 px-4 py-2 bg-[#F8B120] text-white rounded-lg hover:bg-[#e5a11d] transition-colors"
        >
          Riprova
        </button>
      </div>
    )
  }

  if (!analytics) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-8 text-center">
        <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
        <p className="text-gray-500">Nessun dato disponibile</p>
        <p className="text-xs text-gray-400 mt-1">Le statistiche appariranno quando l'articolo ricevera visite</p>
      </div>
    )
  }

  // Find max views for chart scaling
  const maxViews = Math.max(...analytics.dailyBreakdown.map(d => d.views), 1)

  // Calculate percentage change
  const percentChange = analytics.yesterday > 0 
    ? Math.round(((analytics.today - analytics.yesterday) / analytics.yesterday) * 100)
    : analytics.today > 0 ? 100 : 0

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          label="Visualizzazioni Totali"
          value={analytics.total}
          icon={
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
          }
        />
        <StatCard
          label="Oggi"
          value={analytics.today}
          trend={analytics.trend}
          percentChange={percentChange}
          icon={
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          }
        />
        <StatCard
          label="Ultimi 7 giorni"
          value={analytics.last7Days}
          icon={
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          }
        />
        <StatCard
          label="Ultimi 30 giorni"
          value={analytics.last30Days}
          icon={
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          }
        />
      </div>

      {/* Views Chart */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <h3 className="text-sm font-medium text-gray-700 mb-4">Visualizzazioni ultimi 30 giorni</h3>
        
        {analytics.dailyBreakdown.length === 0 ? (
          <div className="h-48 flex items-center justify-center text-gray-400 text-sm">
            Nessun dato da visualizzare
          </div>
        ) : (
          <div className="h-48 flex items-end justify-between gap-1">
            {analytics.dailyBreakdown.slice(-30).map((day) => {
              const height = (day.views / maxViews) * 100
              const date = new Date(day.date)
              const isToday = new Date().toDateString() === date.toDateString()
              
              return (
                <div
                  key={day.date}
                  className="flex-1 flex flex-col items-center group"
                >
                  <div className="relative w-full">
                    {/* Tooltip */}
                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block z-10">
                      <div className="bg-gray-900 text-white text-xs rounded py-1 px-2 whitespace-nowrap">
                        {date.toLocaleDateString('it-IT', { day: 'numeric', month: 'short' })}: {day.views} visite
                      </div>
                    </div>
                    
                    {/* Bar */}
                    <div
                      className={`w-full rounded-t transition-all duration-200 ${
                        isToday ? 'bg-[#F8B120]' : 'bg-blue-400 hover:bg-blue-500'
                      }`}
                      style={{ height: `${Math.max(height, 2)}%`, minHeight: '4px' }}
                    />
                  </div>
                </div>
              )
            })}
          </div>
        )}
        
        {/* X-axis labels */}
        {analytics.dailyBreakdown.length > 0 && (
          <div className="flex justify-between mt-2 text-xs text-gray-400">
            <span>
              {new Date(analytics.dailyBreakdown[0]?.date).toLocaleDateString('it-IT', { day: 'numeric', month: 'short' })}
            </span>
            <span>Oggi</span>
          </div>
        )}
      </div>

      {/* Detailed breakdown */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <h3 className="text-sm font-medium text-gray-700 mb-4">Dettaglio giornaliero</h3>
        <div className="max-h-64 overflow-y-auto">
          <table className="w-full">
            <thead className="text-xs text-gray-500 uppercase border-b">
              <tr>
                <th className="text-left py-2">Data</th>
                <th className="text-right py-2">Visite</th>
              </tr>
            </thead>
            <tbody className="text-sm">
              {analytics.dailyBreakdown.slice().reverse().slice(0, 14).map((day) => {
                const date = new Date(day.date)
                const isToday = new Date().toDateString() === date.toDateString()
                const isYesterday = new Date(Date.now() - 86400000).toDateString() === date.toDateString()
                
                return (
                  <tr key={day.date} className="border-b border-gray-100 last:border-0">
                    <td className="py-2">
                      {isToday ? (
                        <span className="font-medium text-[#F8B120]">Oggi</span>
                      ) : isYesterday ? (
                        <span className="text-gray-600">Ieri</span>
                      ) : (
                        <span className="text-gray-600">
                          {date.toLocaleDateString('it-IT', { weekday: 'short', day: 'numeric', month: 'short' })}
                        </span>
                      )}
                    </td>
                    <td className="py-2 text-right font-medium">{day.views}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

// Stat Card Component
function StatCard({
  label,
  value,
  icon,
  trend,
  percentChange,
}: {
  label: string
  value: number
  icon: React.ReactNode
  trend?: 'up' | 'down' | 'stable'
  percentChange?: number
}) {
  return (
    <div className="bg-white rounded-xl shadow-sm p-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-gray-400">{icon}</span>
        {trend && percentChange !== undefined && (
          <span className={`text-xs font-medium flex items-center ${
            trend === 'up' ? 'text-green-600' : trend === 'down' ? 'text-red-600' : 'text-gray-500'
          }`}>
            {trend === 'up' && (
              <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
              </svg>
            )}
            {trend === 'down' && (
              <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
              </svg>
            )}
            {Math.abs(percentChange)}%
          </span>
        )}
      </div>
      <div className="text-2xl font-bold text-gray-900">{value.toLocaleString('it-IT')}</div>
      <div className="text-xs text-gray-500 mt-1">{label}</div>
    </div>
  )
}

// Image Upload Component
function ImageUpload({
  value,
  onChange,
}: {
  value: string
  onChange: (url: string) => void
}) {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [isUploading, setIsUploading] = useState(false)
  const [isDragOver, setIsDragOver] = useState(false)

  // Compress image before upload (preserves PNG transparency)
  const compressImage = (file: File, maxWidth = 1200, quality = 0.8): Promise<{blob: Blob, type: string, ext: string}> => {
    return new Promise((resolve, reject) => {
      const img = new Image()
      img.onload = () => {
        const canvas = document.createElement('canvas')
        let { width, height } = img
        
        if (width > maxWidth) {
          height = (height * maxWidth) / width
          width = maxWidth
        }
        
        canvas.width = width
        canvas.height = height
        
        const ctx = canvas.getContext('2d')
        if (!ctx) {
          reject(new Error('Canvas not supported'))
          return
        }
        
        // Keep transparency for PNG, use white background for JPEG
        const isPng = file.type === 'image/png'
        if (!isPng) {
          ctx.fillStyle = '#FFFFFF'
          ctx.fillRect(0, 0, width, height)
        }
        
        ctx.drawImage(img, 0, 0, width, height)
        
        const outputType = isPng ? 'image/png' : 'image/jpeg'
        const outputExt = isPng ? 'png' : 'jpg'
        
        canvas.toBlob(
          (blob) => {
            if (blob) resolve({ blob, type: outputType, ext: outputExt })
            else reject(new Error('Compression failed'))
          },
          outputType,
          isPng ? undefined : quality
        )
      }
      img.onerror = reject
      img.src = URL.createObjectURL(file)
    })
  }

  const handleUpload = async (file: File) => {
    setIsUploading(true)
    try {
      // Compress image first
      const { blob: compressedBlob, type, ext } = await compressImage(file)
      const compressedFile = new File([compressedBlob], file.name.replace(/\.[^.]+$/, '.' + ext), {
        type: type
      })
      
      console.log(`Original: ${(file.size / 1024).toFixed(1)}KB, Compressed: ${(compressedFile.size / 1024).toFixed(1)}KB`)
      
      const formData = new FormData()
      formData.append('image', compressedFile)
      const response = await fetch('/upload.php', {
        method: 'POST',
        body: formData,
      })
      if (!response.ok) throw new Error('Upload failed')
      const data = await response.json()
      onChange(data.url)
    } catch (error) {
      console.error('Upload error:', error)
      alert('Errore durante il caricamento. Riprova con un\'immagine più piccola.')
    } finally {
      setIsUploading(false)
    }
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) handleUpload(file)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)
    const file = e.dataTransfer.files?.[0]
    if (file && file.type.startsWith('image/')) handleUpload(file)
  }

  if (value) {
    return (
      <div className="relative">
        <img src={value} alt="Preview" className="w-full h-48 object-cover rounded-lg" />
        <button
          type="button"
          onClick={() => onChange('')}
          className="absolute top-2 right-2 p-2 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
        >
          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    )
  }

  return (
    <>
      <div
        onClick={() => fileInputRef.current?.click()}
        onDrop={handleDrop}
        onDragOver={e => { e.preventDefault(); setIsDragOver(true) }}
        onDragLeave={() => setIsDragOver(false)}
        className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
          isDragOver ? 'border-[#F8B120] bg-yellow-50' : 'border-gray-300 hover:border-[#F8B120]'
        }`}
      >
        {isUploading ? (
          <div className="flex flex-col items-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#F8B120] mb-2"></div>
            <span className="text-sm text-gray-500">Caricamento...</span>
          </div>
        ) : (
          <>
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <p className="mt-2 text-sm text-gray-600">
              Trascina un'immagine o <span className="text-[#F8B120] font-medium">clicca per caricare</span>
            </p>
            <p className="mt-1 text-xs text-gray-500">PNG, JPG, WebP fino a 5MB</p>
          </>
        )}
      </div>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileChange}
        className="hidden"
      />
      <div className="mt-2">
        <input
          type="url"
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder="Oppure inserisci URL immagine..."
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F8B120] focus:border-transparent"
        />
      </div>
    </>
  )
}

export default function ArticleEditorPage() {
  return (
    <ProtectedRoute>
      <ArticleEditorContent />
    </ProtectedRoute>
  )
}
